/**
 * Documentos do profissional (medical-docs-qualification). Enums com os mesmos
 * valores do backend (`00-interfaces.md`); shapes HTTP canônicos pelo Adendo A3.
 * Ids de profissional (`userId`) são `User.id`, não `Doctor.id` (Adendo A8).
 */
export enum ProfessionalDocumentStatus {
  PENDING = 'PENDING',
  IN_REVIEW = 'IN_REVIEW',
  CONCLUDED = 'CONCLUDED'
}

export enum ProfessionalDocumentPendingReason {
  NEVER_SENT = 'NEVER_SENT',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED'
}

export enum ProfessionalDocumentCategory {
  PERSONAL = 'PERSONAL',
  PROFESSIONAL = 'PROFESSIONAL',
  COURSE = 'COURSE',
  OCCUPATIONAL_HEALTH = 'OCCUPATIONAL_HEALTH',
  CONTRACTUAL = 'CONTRACTUAL'
}

export enum ProfessionalDocumentVerification {
  MANUAL = 'MANUAL',
  SOURCE = 'SOURCE',
  BOTH = 'BOTH'
}

export enum ProfessionalDocumentRejectionReason {
  ILLEGIBLE = 'ILLEGIBLE',
  EXPIRED = 'EXPIRED',
  INCOMPLETE = 'INCOMPLETE',
  DIVERGENT_DATA = 'DIVERGENT_DATA',
  WRONG_TYPE = 'WRONG_TYPE',
  OTHER = 'OTHER'
}

export enum ProfessionalSealKey {
  MINIMUM_DOCS = 'MINIMUM_DOCS',
  LIFE_SUPPORT = 'LIFE_SUPPORT'
}

/** Origem da versão (Adendo A5): `BACKFILL` = importada da base da operação (OPS1). */
export enum ProfessionalDocumentVersionSource {
  APP = 'APP',
  BACKFILL = 'BACKFILL'
}

/** Códigos de erro das rotas de revisão que o web trata (`errorCode` do corpo). */
export enum ProfessionalDocumentsErrorCode {
  /** 409 (Adendo A6.2): outro revisor já decidiu ou desfez a versão. */
  DOCUMENT_NOT_IN_REVIEW = 'DOCUMENT_NOT_IN_REVIEW',
  DOCUMENT_EXPIRY_DATE_REQUIRED = 'DOCUMENT_EXPIRY_DATE_REQUIRED',
  DOCUMENT_REJECTION_REASON_REQUIRED = 'DOCUMENT_REJECTION_REASON_REQUIRED',
  DOCUMENT_VERSION_NOT_FOUND = 'DOCUMENT_VERSION_NOT_FOUND',
  /** 404 do detalhe do profissional: inexistente ou sem vínculo ativo no escopo (D26). */
  PROFESSIONAL_NOT_FOUND = 'PROFESSIONAL_NOT_FOUND',
  /** 404 de todas as rotas novas com a flag do backend desligada: "feature indisponível". */
  PROFESSIONAL_DOCUMENTS_DISABLED = 'PROFESSIONAL_DOCUMENTS_DISABLED'
}

export type ReviewQueueTag = 'NEW' | 'URGENT' | 'CONCLUDED' | 'BLOCKS_ALLOCATION'

export interface DocumentRejection {
  reasons: ProfessionalDocumentRejectionReason[]
  otherReason: string | null
  guidance: string | null
}

export interface DocumentVersionSummary {
  versionId: number
  versionNumber: number
  submittedAt: string
  pageCount: number | null
  sizeBytes: number | null
  source: ProfessionalDocumentVersionSource
}

export type DocumentHistoryKind = 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'UNDONE'

export interface DocumentHistoryEvent {
  kind: DocumentHistoryKind
  at: string
  versionNumber: number | null
  /**
   * Revisor (D26). `null` em eventos do médico/sistema, em versões importadas (A5) e em
   * decisões de empresa fora do escopo de quem vê (D80).
   */
  reviewerName: string | null
  /** Empresa do revisor (D26). `null` nos mesmos casos de `reviewerName`. */
  companyName: string | null
  /** Origem da versão do evento; `BACKFILL` mostra "Importado da base da operação" (A5). */
  source: ProfessionalDocumentVersionSource
  detail: string | null
}

export interface ProfessionalSealStatus {
  key: ProfessionalSealKey
  name: string
  satisfied: boolean
  total: number
  concluded: number
  crmActive: boolean | null
}

export interface ProfessionalDocumentItem {
  /** `null` = tipo do catálogo nunca enviado. */
  documentId: number | null
  typeKey: string
  name: string
  /** Texto "Como conferir" do catálogo. */
  description: string | null
  category: ProfessionalDocumentCategory
  hasExpiry: boolean
  acceptsAuthenticityCode: boolean
  verification: ProfessionalDocumentVerification
  sealKeys: ProfessionalSealKey[]
  status: ProfessionalDocumentStatus
  pendingReason: ProfessionalDocumentPendingReason | null
  isLegacy: boolean
  expiresAt: string | null
  authenticityCode: string | null
  rejection: DocumentRejection | null
  /** `null` também quando o documento não é compartilhado com o escopo (D80): sem abrir/aprovar. */
  currentVersion: DocumentVersionSummary | null
  /** Versão cuja decisão pode ser desfeita agora; `null` = sem "Desfazer". */
  undoableVersionId: number | null
  history: DocumentHistoryEvent[]
}

export interface ProfessionalCompanyRef {
  id: number
  name: string
}

export interface NextStationRef {
  /** ISO com o offset do plantão (ex.: `2026-10-10T01:00:00.000-03:00`): formatar sem converter para UTC. */
  start: string
  scheduleName: string
}

export interface ProfessionalDocumentsDetailResponse {
  userId: number
  name: string
  crm: string | null
  crmUf: string | null
  crmActive: boolean | null
  /** Foto do profissional (`user.avatarUrl` já resolvido pelo BE); `null` = sem foto (avatar padrão). */
  avatarUrl: string | null
  isLegacy: boolean
  companies: ProfessionalCompanyRef[]
  nextStation: NextStationRef | null
  seals: ProfessionalSealStatus[]
  documents: ProfessionalDocumentItem[]
  /**
   * Declaração de vínculo vigente (D27, D64); `null` = o médico ainda não respondeu.
   * Ausente = backend anterior ao campo (o painel não mostra o bloco).
   */
  employmentDeclaration?: EmploymentDeclaration | null
}

export enum EmploymentDeclarationKind {
  SPDM = 'SPDM',
  PUBLIC_SERVANT_MUNICIPAL = 'PUBLIC_SERVANT_MUNICIPAL',
  PUBLIC_SERVANT_STATE = 'PUBLIC_SERVANT_STATE',
  PUBLIC_SERVANT_FEDERAL = 'PUBLIC_SERVANT_FEDERAL',
  OTHER = 'OTHER'
}

export interface EmploymentDeclarationEntry {
  kind: EmploymentDeclarationKind
  organizationName: string
  /** "aaaa-mm-dd" */
  startDate: string | null
  /** "aaaa-mm-dd" */
  endDate: string | null
}

export interface EmploymentDeclaration {
  id: number
  /** Instante ISO da resposta. */
  declaredAt: string
  workedLast18Months: boolean
  entries: EmploymentDeclarationEntry[]
}

export interface ReviewQueueDocumentSummary {
  documentId: number
  typeKey: string
  name: string
  status: ProfessionalDocumentStatus
  pendingReason: ProfessionalDocumentPendingReason | null
  isLegacy: boolean
  hasExpiry: boolean
  acceptsAuthenticityCode: boolean
  currentVersionId: number | null
  submittedAt: string | null
}

export interface ReviewQueueProfessional {
  userId: number
  name: string
  crm: string | null
  crmUf: string | null
  /** Foto do profissional (`user.avatarUrl` já resolvido pelo BE); `null` = sem foto (avatar padrão). */
  avatarUrl: string | null
  companies: ProfessionalCompanyRef[]
  tags: ReviewQueueTag[]
  /** Envio Em Análise mais antigo do profissional (D23), ISO UTC. */
  waitingSince: string | null
  documents: ReviewQueueDocumentSummary[]
}

export interface ReviewQueueParams {
  search?: string
  tag?: ReviewQueueTag
  status?: ProfessionalDocumentStatus
  typeKey?: string
  page?: number
  /** Backend: padrão 20, máximo 100. */
  limit?: number
}

export interface ReviewQueueResponse {
  items: ReviewQueueProfessional[]
  meta: { total: number; page: number; limit: number }
}

export interface ReviewQueueSummaryResponse {
  inReview: number
  urgent: number
  oldestWaitingSince: string | null
}

export interface ApproveVersionPayload {
  /** `aaaa-mm-dd`, obrigatório quando o tipo tem validade (D10). */
  expiresAt?: string
  authenticityCode?: string
}

export interface RejectVersionPayload {
  reasons: ProfessionalDocumentRejectionReason[]
  otherReason?: string
  guidance?: string
}

export interface BulkApprovePayload {
  items: Array<{ versionId: number } & ApproveVersionPayload>
}

export interface BulkRejectPayload extends RejectVersionPayload {
  versionIds: number[]
}

export interface BulkDecisionFailure {
  versionId: number
  errorCode: string
  message: string
}

export interface BulkDecisionResponse {
  succeeded: number[]
  failed: BulkDecisionFailure[]
}

/** `GET /professional-documents/catalog` (Adendo A3.1). */
export interface DocumentTypeCatalogItem {
  key: string
  name: string
  description: string | null
  category: ProfessionalDocumentCategory
  hasExpiry: boolean
  isSensitive: boolean
  acceptsAuthenticityCode: boolean
  sealKeys: ProfessionalSealKey[]
}

export interface SealCatalogItem {
  key: ProfessionalSealKey
  name: string
  /** Texto vazio quando o selo não tem descrição. */
  description: string
  typeKeys: string[]
}

export interface ProfessionalDocumentCatalogResponse {
  types: DocumentTypeCatalogItem[]
  seals: SealCatalogItem[]
}
